import React, { useState } from 'react'
import { Badge, ButtonGroup, Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons'

function Analytics({ params, setParams, availableAnalytics = [], canEdit = true, showAsAccordion = false, isRequired = false, label = 'Analytics' }) {
  const analytics = params.analytics || []
  const excludedAnalytics = params.excludedAnalytics || []

  // Start expanded if there are any pre-selected analytics
  const hasPreselectedAnalytics = analytics.length > 0 || excludedAnalytics.length > 0
  const [isExpanded, setIsExpanded] = useState(isRequired || hasPreselectedAnalytics)

  // Get the state of each analytic (off, include, or exclude)
  const getAnalyticState = (analyticId) => {
    if (analytics.some(a => a.id === analyticId)) return 'include'
    if (excludedAnalytics.some(a => a.id === analyticId)) return 'exclude'
    return 'off'
  }

  const handleAnalyticChange = (analytic, newState) => {
    if (!canEdit) return

    setParams((prev) => {
      const newAnalytics = prev.analytics.filter(a => a.id !== analytic.id)
      const newExcludedAnalytics = (prev.excludedAnalytics || []).filter(a => a.id !== analytic.id)

      if (newState === 'include') {
        return {
          ...prev,
          analytics: [...newAnalytics, { ...analytic, options: 'include' }],
          excludedAnalytics: newExcludedAnalytics
        }
      } else if (newState === 'exclude') {
        return {
          ...prev,
          analytics: newAnalytics,
          excludedAnalytics: [...newExcludedAnalytics, { ...analytic, options: 'exclude' }]
        }
      } else {
        // off
        return {
          ...prev,
          analytics: newAnalytics,
          excludedAnalytics: newExcludedAnalytics
        }
      }
    })
  }

  // Format analytics value for display
  const formatAnalyticsValue = () => {
    const includedCount = analytics.length
    const excludedCount = excludedAnalytics.length

    if (includedCount === 0 && excludedCount === 0) {
      return 'Not set'
    }

    const parts = []
    if (includedCount > 0) {
      parts.push(`${includedCount} included`)
    }
    if (excludedCount > 0) {
      parts.push(`${excludedCount} excluded`)
    }
    return parts.join(', ')
  }

  const content = (
    <div>
      {availableAnalytics.map((analytic) => {
        const state = getAnalyticState(analytic.id)

        return (
          <div key={analytic.id} className="mb-3 p-3 border rounded">
            <div className="mb-2">
              <strong>{analytic.name}</strong>
              {analytic.description && (
                <div className="small text-muted">{analytic.description}</div>
              )}
            </div>
            <div>
              <div className="mb-2">
                <strong className="small">Filter</strong>
              </div>
              <ButtonGroup className="w-100">
                <Button
                  variant={state === 'off' ? 'primary' : 'light'}
                  size="sm"
                  onClick={() => handleAnalyticChange(analytic, 'off')}
                  disabled={!canEdit}
                >
                  Off
                </Button>
                <Button
                  variant={state === 'include' ? 'primary' : 'light'}
                  size="sm"
                  onClick={() => handleAnalyticChange(analytic, 'include')}
                  disabled={!canEdit}
                >
                  Include
                </Button>
                <Button
                  variant={state === 'exclude' ? 'primary' : 'light'}
                  size="sm"
                  onClick={() => handleAnalyticChange(analytic, 'exclude')}
                  disabled={!canEdit}
                >
                  Exclude
                </Button>
              </ButtonGroup>
              <div className="mt-2 small text-muted">
                {state === 'off'
                  ? 'No filter applied'
                  : state === 'include'
                  ? 'Show transactions with this flag'
                  : 'Hide transactions with this flag'}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )

  if (showAsAccordion) {
    return (
      <div className="mb-2">
        {/* Accordion Header */}
        <div
          className={`d-flex justify-content-between align-items-center p-3 border rounded ${
            isRequired ? 'border-warning' : ''
          }`}
          style={{ cursor: 'pointer', backgroundColor: isExpanded ? '#f8f9fa' : 'white' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="d-flex align-items-center gap-3 flex-grow-1">
            <div className="d-flex align-items-center gap-2">
              <strong>{label}</strong>
              {isRequired && (
                <Badge bg="warning" text="dark" className="small">
                  Required
                </Badge>
              )}
            </div>
            {!isExpanded && (
              <div className="text-muted small">{formatAnalyticsValue()}</div>
            )}
          </div>
          <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className="text-muted" />
        </div>

        {/* Accordion Content */}
        {isExpanded && (
          <div className="p-3 border border-top-0 rounded-bottom">
            {content}
          </div>
        )}
      </div>
    )
  }

  return content
}

export default Analytics
